import { Tabs, Tooltip } from 'antd';
import { useFormikContext } from 'formik';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { type MobilityModuleSpecsFragment } from '../../../../api/fragments/MobilityModuleSpecs';
import { useMultipleDealerIds } from '../../../../components/contexts/DealerContextManager';
import NoItemResult from '../../../../components/results/NoItemResult';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import MobilityModuleEmailForm from './MobilityEmailContent';
import { type MobilityEmailContentFormikValue } from './shared';

type MobilityEmailScenarioContentInnerTabProps = {
    sendEmailToCustomer: boolean;
    locationId?: string;
    isHomeDelivery?: boolean;
    prefix: string;
    targetDealerId?: string;
    companyId: string;
    defaultValueDisabled: boolean;
    disabled?: boolean;
};

type MobilityEmailScenarioContentProps = {
    sendEmailToCustomer: boolean;
    locations: MobilityModuleSpecsFragment['locations'];
    isHomeDeliveryEnabled: boolean;
    hideTab?: boolean;
    emailContents: MobilityModuleSpecsFragment['mobilityEmailContents'];
    targetDealerId?: string;
    companyId?: string;
    defaultValueDisabled: boolean;
    disabled?: boolean;
};

const HOME_DELIVERY_TAB = 'homeDelivery';

const StyledTabs = styled(Tabs)`
    .ant-tabs-tab-btn {
        max-width: 180px;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }
`;

const MobilityEmailScenarioContentInnerTab = ({
    prefix,
    sendEmailToCustomer,
    locationId,
    isHomeDelivery,
    targetDealerId,
    companyId,
    defaultValueDisabled,
    disabled,
}: MobilityEmailScenarioContentInnerTabProps) => {
    const { t } = useTranslation(['mobilityModuleDetails', 'common', 'emails']);

    const { dealerIdsByCompanyId } = useMultipleDealerIds();

    const dealerIds = useMemo(
        () => (targetDealerId ? [targetDealerId] : dealerIdsByCompanyId(companyId)),
        [companyId, dealerIdsByCompanyId, targetDealerId]
    );

    return (
        <CollapsibleWrapper defaultActiveKey={[`emailContents_customers`, `emailContents_operators`]}>
            {sendEmailToCustomer && (
                <Panel key="emailContents_customers" header={t('mobilityModuleDetails:tabs.panels.customers.title')}>
                    <MobilityModuleEmailForm
                        dealerIds={dealerIds}
                        defaultValueDisabled={defaultValueDisabled}
                        disabled={disabled}
                        headerKey="emailContents.customers.bookingConfirmation"
                        name={`${prefix}.customers.bookingConfirmation`}
                    />
                    <MobilityModuleEmailForm
                        dealerIds={dealerIds}
                        defaultValueDisabled={defaultValueDisabled}
                        disabled={disabled}
                        headerKey="emailContents.customers.bookingAmendment"
                        name={`${prefix}]customers.bookingAmendment`}
                    />
                    <MobilityModuleEmailForm
                        dealerIds={dealerIds}
                        defaultValueDisabled={defaultValueDisabled}
                        disabled={disabled}
                        headerKey="emailContents.customers.bookingCancellation"
                        name={`${prefix}]customers.bookingCancellation`}
                    />
                    <MobilityModuleEmailForm
                        dealerIds={dealerIds}
                        defaultValueDisabled={defaultValueDisabled}
                        disabled={disabled}
                        headerKey="emailContents.customers.bookingReminder"
                        name={`${prefix}]customers.bookingReminder`}
                    />
                    <MobilityModuleEmailForm
                        dealerIds={dealerIds}
                        defaultValueDisabled={defaultValueDisabled}
                        disabled={disabled}
                        headerKey="emailContents.customers.bookingCheckedIn"
                        name={`${prefix}]customers.bookingCheckedIn`}
                    />
                    <MobilityModuleEmailForm
                        dealerIds={dealerIds}
                        defaultValueDisabled={defaultValueDisabled}
                        disabled={disabled}
                        headerKey="emailContents.customers.bookingComplete"
                        name={`${prefix}]customers.bookingComplete`}
                    />
                </Panel>
            )}
            <Panel key="emailContents_operators" header={t('mobilityModuleDetails:tabs.panels.operators.title')}>
                <MobilityModuleEmailForm
                    dealerIds={dealerIds}
                    defaultValueDisabled={defaultValueDisabled}
                    disabled={disabled}
                    headerKey="emailContents.operators.bookingConfirmation"
                    name={`${prefix}]operators.bookingConfirmation`}
                />
                <MobilityModuleEmailForm
                    dealerIds={dealerIds}
                    defaultValueDisabled={defaultValueDisabled}
                    disabled={disabled}
                    headerKey="emailContents.operators.bookingAmendment"
                    name={`${prefix}]operators.bookingAmendment`}
                />
                <MobilityModuleEmailForm
                    dealerIds={dealerIds}
                    defaultValueDisabled={defaultValueDisabled}
                    disabled={disabled}
                    headerKey="emailContents.operators.bookingCancellation"
                    name={`${prefix}]operators.bookingCancellation`}
                />
            </Panel>
        </CollapsibleWrapper>
    );
};

const MobilityEmailScenarioContent = ({
    sendEmailToCustomer,
    locations,
    isHomeDeliveryEnabled,
    hideTab,
    emailContents,
    companyId,
    targetDealerId,
    defaultValueDisabled,
    disabled,
}: MobilityEmailScenarioContentProps) => {
    const { t } = useTranslation(['mobilityModuleDetails', 'common']);

    const { values } = useFormikContext<MobilityEmailContentFormikValue>();

    const [activeTab, setActiveTab] = useState<string | null>(() => {
        if (locations?.[0]?.id) {
            return locations?.[0]?.id;
        }

        if (isHomeDeliveryEnabled) {
            return HOME_DELIVERY_TAB;
        }

        return null;
    });

    const tabPanes = useMemo(
        () =>
            [
                ...(locations ?? [])
                    .filter(location => emailContents.some(content => content.locationId === location.id))
                    .map(location => (
                        <Tabs.TabPane
                            key={location.id}
                            tab={
                                <Tooltip title={location.name}>
                                    <span>{location.name}</span>
                                </Tooltip>
                            }
                        />
                    )),
                isHomeDeliveryEnabled && (
                    <Tabs.TabPane
                        key={HOME_DELIVERY_TAB}
                        tab={
                            <Tooltip title={t('common:homeDelivery')}>
                                <span>{t('common:homeDelivery')}</span>
                            </Tooltip>
                        }
                    />
                ),
            ].filter(Boolean),
        [isHomeDeliveryEnabled, locations, t, emailContents]
    );

    const prefix = useMemo(() => {
        if (activeTab === HOME_DELIVERY_TAB) {
            const foundIndex = values.emailContents?.findIndex(item => item.isHomeDelivery);

            return `emailContents[${foundIndex}]`;
        }

        const foundIndex = values.emailContents?.findIndex(
            item => item.locationId === activeTab && !item.isHomeDelivery
        );

        return `emailContents[${foundIndex}]`;
    }, [values.emailContents, activeTab]);

    return (
        <>
            {!hideTab && tabPanes.length > 0 && <StyledTabs onChange={setActiveTab}>{tabPanes}</StyledTabs>}
            {tabPanes.length > 0 && (
                <MobilityEmailScenarioContentInnerTab
                    key={activeTab}
                    companyId={companyId}
                    defaultValueDisabled={defaultValueDisabled}
                    disabled={disabled}
                    isHomeDelivery={activeTab === HOME_DELIVERY_TAB && !hideTab}
                    locationId={activeTab === HOME_DELIVERY_TAB || hideTab ? null : activeTab}
                    prefix={prefix}
                    sendEmailToCustomer={sendEmailToCustomer}
                    targetDealerId={targetDealerId}
                />
            )}
            {tabPanes.length === 0 && <NoItemResult subTitle={t('mobilityModuleDetails:noLocation')} />}
        </>
    );
};

export default MobilityEmailScenarioContent;
